<template>
  <div class="issues" :class="userRoleGroup" v-if="userRoleGroup">
    <centralized-issues-filters-header
      :project-id='detailedProject.id'
      @loadIssues='loadIssues'
    />
    <centralized-issues-data-view
      :issues-list='issuesFormatted'
      :for-project='true'
      @columnSorted='columnSorted'
      @changedSelectedPage='changedSelectedPage'
      @changedItemsAmountPerPage='changedItemsAmountPerPage'
      @downloadCSVReport='downloadCSVReport'
      @loadIssues='loadIssues'
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { views } from '@/router/constants';
import { userRoleGroups } from '@/constants/auth';
import { projectStatuses } from '@/constants/projects';
import { ISSUE_STATUSES } from '@/constants/issues';
import CentralizedIssuesFiltersHeader from '@/components/CentralizedIssues/CentralizedIssuesFiltersHeader.vue';
import CentralizedIssuesDataView from '@/components/CentralizedIssues/CentralizedIssuesDataView.vue';
import { CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS } from '@/store/centralizedIssues/actionTypes';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import { downloadIssuesCSVReport } from '@/services/repository';
import { issueStatusKey } from '@/utils/suppression';

export default {
  name: 'AutomaticProjectIssuesList',
  components: {
    CentralizedIssuesDataView,
    CentralizedIssuesFiltersHeader,
  },
  data() {
    return {
      page: 1,
      selectedIssue: null,
      showIssueDetailsPopup: false,
      polling: null,
    };
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  computed: {
    ...mapGetters([
      'userRoleGroup',
      'detailedProject',
      'centralizedIssues',
      'centralizedIssuesForProjectDetails',
      'centralizedIssuesListOptions',
      'projectIssuesRefId',
      'projectIssuesRunningInterval',
      'projectIssuesProgress',
    ]),
    issueStatuses() {
      return ISSUE_STATUSES;
    },
    issuesFormatted() {
      return this.centralizedIssues.map(issue => ({
        id: issue.id,
        date: issue.createdDate,
        name: issue.name,
        displayName: issue.displayName,
        tld: issue.tld,
        asset: issue.asset,
        description: issue.metaData.description || 'N/A',
        severity: issue.severity,
        probability: issue.probability,
        status: issueStatusKey(issue),
        issueStatus: issue.status,
        suppression: issue.suppression || null,
        checksum: issue.checksum,
        priority: issue.priority,
        cycles: issue.issueCount,
        firstSeen: issue.firstDate,
        lastSeen: issue.lastDate,
        metaData: issue.metaData,
      }));
    },
    userGroups() {
      return userRoleGroups;
    },
    isUserSP() {
      return this.userRoleGroup === userRoleGroups.SP;
    },
  },
  watch: {
    userRoleGroup() {
      if (this.isUserSP && this.detailedProject.status !== projectStatuses.IN_PROGRESS) {
        this.$router.replace({ name: views.PROJECT_GENERAL_INFORMATION });
      }
    },
  },
  methods: {
    clearInterval() {
      clearInterval(this.polling);
      this.polling = null;
    },
    async loadIssues(payload = {}, turnOnLoading = true) {
      this.clearInterval();
      await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
        forProjectDetails: true,
        page: this.page,
        limit: this.itemsPerPage,
        projectId: this.detailedProject.id,
        turnOnLoading,
        ...payload,
      });
      if (this.projectIssuesRefId && this.projectIssuesRefId !== '') {
        this.polling = setInterval(async () => {
          if (!this.projectIssuesRefId || this.projectIssuesRefId === '') {
            this.clearInterval();
          } else if (!this.projectIssuesRunningInterval) {
            await this.$store.dispatch(CHANGE_CENTRALIZES_ISSUES_LIST_OPTIONS, {
              forProjectDetails: true,
              page: this.page,
              limit: this.itemsPerPage,
              projectId: this.detailedProject.id,
              turnOnLoading,
              refId: this.projectIssuesRefId,
              ...payload,
            });
          }
        }, 3000);
      }
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.loadIssues({
        forProjectDetails: true,
        page: this.page,
        projectId: this.detailedProject.id,
        turnOnLoading: true,
      });
    },
    async downloadCSVReport() {
      const payload = {
        projectId: this.detailedProject.id,
        search: this.centralizedIssuesListOptions.search,
        specificSearch: this.centralizedIssuesListOptions.toggles.specificSearch,
        onlyCVEs: this.centralizedIssuesListOptions.toggles.onlyCVEs,
        hideSuppress: this.centralizedIssuesListOptions.toggles.hideSuppress,
        hideFixed: this.centralizedIssuesListOptions.toggles.hideFixed,
      };
      const scanType = this.detailedProject.testCard.type.replace(/^[a-z]/, match => match.toUpperCase());
      const {tld} = this.detailedProject.testCard;
      const dataStream = await downloadIssuesCSVReport(payload);
      const blob = new Blob([dataStream], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `${scanType}-${tld}-${Date.now()}-issues.csv`);
      document.body.appendChild(link);
      link.click();
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'centralizedIssues' });
      await this.loadIssues({
        projectId: this.detailedProject.id,
        forProjectDetails: true,
        limit: itemsPerPage,
        page: this.page,
        turnOnLoading: true,
      });
    },
    async columnSorted({ column, direction }) {
      await this.loadIssues({
        forProjectDetails: true,
        page: this.page,
        limit: this.itemsPerPage,
        projectId: this.detailedProject.id,
        search: this.centralizedIssuesListOptions.search,
        sort: direction
          ? {
            [column.sortingField || column.field]: direction,
          }
          : {},
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';
.issues {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  .issues-tab-loading {
    align-self: center;
    justify-self: center;
    margin-top: 20%;
  }
  .domain-select {
    width: rem(350);
    margin-top: rem(10);
    margin-left: rem(30);
    margin-bottom: rem(40);
  }
  .issues-filters-header {
    display: flex;
    flex-direction: row;
    margin-bottom: rem(15);
    .issue-search {
      min-width: rem(400);
      margin-left: rem(15);
      margin-top: rem(12);
    }
    .filter-switch {
      margin-left: rem(40);
      margin-top: rem(35);
    }
  }
  .counter{
    padding-left: rem(32);
    margin-top: rem(-30);
    margin-bottom: rem(15);
    font-family: $font-family-regular;
    font-size: rem(12);
    text-transform: uppercase;
    color: var(--text-secondary);
  }
  .issues-list-header {
    display: flex;
    justify-content: space-between;
    padding-left: rem(32);
    padding-top: rem(32);
    margin-bottom: rem(30);
    padding-right: rem(32);
    .download-csv-btn {
      height: rem(32);
      margin-right: rem(25);
      &:hover {
        cursor: pointer;
      }
    }
    .title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: rem(22);
      font-weight: bold;
      line-height: rem(20);
      letter-spacing: 0;
    }
    .actions {
      display: flex;
      flex-direction: row;
    }
    .priority-sort {
      border: solid rem(1) transparent;
      border-radius: $radius-sm;
      height: rem(30);
      padding: rem(2) rem(25) rem(2) rem(2);

      &:hover {
        cursor: pointer;
        background-color: var(--bg-tertiary);
      }
    }
  }
  .issues-list-content {
    &__footer{
      margin-top: rem(25);
      margin-bottom: rem(45);
    }
    &.loading {
      display: none;
    }
  }
  .export_table {
    flex: 1;
    .download-csv-report {
      float: right;
      height: rem(16);
      width: rem(130);
      font-family: $font-family-medium;
      color: var(--brand-primary);
      font-size: rem(12);
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: rem(16);
      text-align: center;
    }
  }
  .issues_table {
    &__footer{
      padding-top: rem(20);
    }
    .wombat-table {
      .wombat-overlay {
        border-bottom: none;
        &:hover {
          background-color: var(--bg-tertiary);
          --display: block;
          --bg: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(244, 244, 248, 0) 78%, rgba(230, 230, 239, 0.97) 91%, var(--bg-tertiary) 100%);
          --displayissuecountcell: flex;
        }
      }
    }
  }
  .issues_table-table {
    flex: 0 0 90%;
  }

  ::v-deep.wombat-table {
    height: 100% !important;
  }

  ::v-deep.wombat-header {
    padding-top: rem(10);
  }

  ::v-deep.wombat-row {
    .status.column.issue-status {
      top: auto;
    }

    .actions {
      display: none;
      width: max-content;

      .action {
        display: inline;
        padding: rem(6) rem(2) rem(7);
      }
    }
    .mark-as-fixed {
      display: none;
    }

    .mark-issue-as-fixed-button {
      top: rem(-2);
      right: rem(21);
    }

    &:hover {
      .issue-status.class-column-wrapper {
        display: none;
      }
      .actions {
        display: inline-block;
      }
      .mark-as-fixed {
        display: inline-block;
      }
    }

    .issue-risk-score {
      padding-top: rem(18);
    }

    .change-issue-status-button {
      top: rem(-2);
      right: rem(20);
    }
  }

  .issues-header {
    display: block;
    padding-left: rem(15);
    padding-top: rem(16);

    .header {
      display: inline-block;

      color: var(--text-tertiary);
      font-size: rem(10);
      letter-spacing: rem(0.8);
      line-height: rem(10);
      text-transform: uppercase;
    }
  }

  .probability {
    width: rem(120);
    text-transform: capitalize;
  }

  .severity {
    width: rem(108);
    text-transform: capitalize;
  }

  .risk-score {
    width: rem(115);
  }

  .status {
    width: rem(68);
    text-transform: capitalize;
    display: inline-block;
    text-align: right;
  }

  .column {
    padding-top: rem(20);
    padding-right: rem(5);
  }

  .new-issue-button {
    position: absolute;
    color: $white;
    font-size: rem(12);
    font-weight: 500;
    letter-spacing: rem(1.07);
    line-height: rem(16);
    text-align: center;
    top: rem(-40);
    right: rem(86);
  }
}
</style>
<style lang='scss'>
@import '~@/assets/styles/design-system';

.issues {
  .search-animation-wrapper{
    margin-top: rem(-5);
    &:hover {
      cursor: pointer;
    }
  }
  .search-animation{
    height: rem(30);
  }
  .probability {
    line-height: unset;
  }
  .severity {
    line-height: unset;
  }
  .wombat-table {
    --displayactioncell:none;
    padding: rem(15) 0;
    .wombat-header{
      padding-bottom: rem(10);
    }
    .wombat-row {
      border-top: none !important;
      border-left: none !important;
      border-right: none !important;
    }
    .wombat-overlay {
      border-bottom: none;
      &:hover{
        background-color: var(--bg-tertiary);
        --display: block;
        --bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(244,244,248,0) 78%, rgba(230,230,239,0.97) 91%, var(--bg-tertiary) 100%);
        --displayactioncell: flex;
      }
    }
  }
}
.issue-search{
  .v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control > .v-input__slot {
    box-shadow: none;
    border: rem(1) solid var(--border-primary);
    border-radius: $radius-xs;
  }
  .v-input__icon.v-input__icon--append {
    margin-top: 0 !important;
  }
  .v-text-field.v-text-field--solo .v-input__control {
    min-height: rem(40);
  }
}
</style>
